/**
 * The web's view of a ticket. The API (packages/api/src/services/organization/
 * tickets) owns the shape; this mirrors it and carries the presentation
 * vocabulary — labels, colours, and the client-side copy of "which actions may
 * I offer" so the buttons beside a ticket match what the server will accept.
 */

export const TICKET_KINDS = [
  "decision",
  "approval",
  "info",
  "asset",
  "access",
  "other",
  // The feedback kinds (API: FEEDBACK_KINDS) — about a client or a module, for
  // the developers who own it. Mostly filed by a person's own assistant under
  // `tickets:file`; listed here so the desk can label, filter and file them too.
  "feedback",
  "request",
  "idea",
  "review",
  "bug",
] as const;
export type TicketKind = (typeof TICKET_KINDS)[number];

/**
 * The feedback kinds — the API's `FEEDBACK_KINDS` (ticket-lifecycle.ts),
 * spelled twice because the halves cannot import each other. Pinned equal by
 * tickets.types.test.ts against the tail of TICKET_KINDS.
 */
export const FEEDBACK_KINDS = [
  "feedback",
  "request",
  "idea",
  "review",
  "bug",
] as const;
export const isFeedbackKind = (kind: string | null | undefined): boolean =>
  (FEEDBACK_KINDS as readonly string[]).includes(kind ?? "");

/**
 * The queue engineering work rides — the API's `FEEDBACK_TEAM`
 * (ticket-lifecycle.ts), spelled twice for the reason FEEDBACK_KINDS is. A
 * feedback kind is filed to it when it names nobody, and rides it BESIDE the
 * person when it names one (tickets.class.ts `filingTeam`), so the dialog can
 * say where a draft will go before it is filed.
 */
export const ENGINEERING_TEAM = "engineering";

export const TICKET_PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

export const TICKET_STATUSES = [
  "open",
  "acknowledged",
  "in_progress",
  "waiting_on_requester",
  "done",
  "declined",
  "cancelled",
] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const TICKET_ACTIONS = [
  "ack",
  "start",
  "wait",
  "resume",
  "done",
  "decline",
  "cancel",
  "reopen",
  "assign",
  "comment",
  "set-due",
] as const;
export type TicketAction = (typeof TICKET_ACTIONS)[number];

/** The desk's four tabs, in the order the header lists them. */
export const TICKET_SCOPES = ["mine", "asked", "team", "all"] as const;
export type TicketScope = (typeof TICKET_SCOPES)[number];

/** True for one of the four tab names — what a link's `?scope=` must be before the desk obeys it. */
export const isTicketScope = (value: unknown): value is TicketScope =>
  typeof value === "string" && (TICKET_SCOPES as readonly string[]).includes(value);

export interface TicketRecord {
  id: number;
  title: string;
  body: string | null;
  kind: TicketKind | string;
  priority: TicketPriority | string;
  status: TicketStatus | string;
  requesterId: number;
  assigneeId: number | null;
  team: string | null;
  dueAt: string | null;
  clientId: string | null;
  governedSlug: string | null;
  entityType: string | null;
  entityId: string | null;
  acknowledgedAt: string | null;
  escalatedAt: string | null;
  reopenedAt: string | null;
  resolvedAt: string | null;
  resolution: string | null;
  createdAt: string;
  updatedAt: string;
  requesterName?: string | null;
  assigneeName?: string | null;
  /** `clients.code` for `clientId`, resolved by the API on the way out. */
  clientCode?: string | null;
  /**
   * Since when NOBODY HAS PICKED IT UP — no acknowledgement, no start and no
   * work linked from it since it was filed or last reopened — or null once
   * somebody has (and on a closed ticket). Derived by the API
   * (ticket-pickup.ts); never a claim that nobody has SEEN it.
   */
  awaitingPickupSince?: string | null;
  /**
   * WHO REPORTED IT, when a client sent it in: the client's own person, as
   * their instance or their e-mail named them — client-side identity, never an
   * HQ account (the requester is the HQ account it was filed as). Null on a
   * ticket filed in HQ. Derived by the API from the intake that filed it.
   */
  reporterName?: string | null;
  reporterEmail?: string | null;
  /** How it came in: `feedback` (a client instance's in-app feedback) or `mail` (support e-mail). */
  intakeChannel?: IntakeChannel | string | null;
}

/** The two ways a client's own submission reaches HQ (API: ticket-intake/intake-ledger.ts). */
export type IntakeChannel = "feedback" | "mail";

export const INTAKE_CHANNEL_LABEL: Record<IntakeChannel, string> = {
  feedback: "In-app feedback",
  mail: "Support e-mail",
};

/** "Kim Lee <kim@example.com>", the address, the name — or null on a ticket filed in HQ. */
export const reporterOf = (
  t: Pick<TicketRecord, "reporterName" | "reporterEmail" | "intakeChannel">,
): string | null => {
  if (!t.intakeChannel) return null;
  const name = (t.reporterName ?? "").trim();
  const email = (t.reporterEmail ?? "").trim();
  if (name && email) return `${name} <${email}>`;
  return email || name || "the client";
};

/** How a channel reads beside the reporter ("via support e-mail"). */
export const intakeLabel = (channel: string | null | undefined): string =>
  channel && channel in INTAKE_CHANNEL_LABEL
    ? INTAKE_CHANNEL_LABEL[channel as IntakeChannel]
    : "Sent in by the client";

export interface TicketEventRecord {
  id: number;
  ticketId: number;
  kind:
    | "created"
    | "comment"
    | "status"
    | "assign"
    | "due"
    | "escalated"
    | "reminder"
    | "link"
    | string;
  actorId: number | null;
  actorName: string | null;
  body: string | null;
  fromStatus: string | null;
  toStatus: string | null;
  toUserId: number | null;
  createdAt: string | null;
}

export interface StaffUser {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  team?: string | null;
  tier?: string | null;
  adminUser?: boolean;
}

export const displayName = (u: StaffUser | null | undefined): string => {
  const full = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
  return full || u?.email || (u ? `#${u.id}` : "");
};

export const ticketHandle = (id: number): string => `TKT-${id}`;

export const KIND_LABEL: Record<TicketKind, string> = {
  decision: "Decision",
  approval: "Approval",
  info: "Information",
  asset: "Asset",
  access: "Access",
  other: "Other",
  feedback: "Feedback",
  request: "Feature request",
  idea: "Idea",
  review: "Review",
  bug: "Bug",
};

export const STATUS_META: Record<
  TicketStatus,
  {
    label: string;
    color: "default" | "primary" | "info" | "warning" | "success" | "error";
  }
> = {
  open: { label: "Open", color: "primary" },
  acknowledged: { label: "Acknowledged", color: "info" },
  in_progress: { label: "In progress", color: "info" },
  waiting_on_requester: { label: "Waiting on requester", color: "warning" },
  done: { label: "Done", color: "success" },
  declined: { label: "Declined", color: "default" },
  cancelled: { label: "Cancelled", color: "default" },
};

export const PRIORITY_META: Record<
  TicketPriority,
  { label: string; color: "default" | "warning" | "error" }
> = {
  low: { label: "Low", color: "default" },
  normal: { label: "Normal", color: "default" },
  high: { label: "High", color: "warning" },
  urgent: { label: "Urgent", color: "error" },
};

export const ACTION_LABEL: Record<TicketAction, string> = {
  ack: "Acknowledge",
  start: "Start",
  wait: "Wait on requester",
  resume: "Resume",
  done: "Done",
  decline: "Decline",
  cancel: "Cancel ticket",
  reopen: "Reopen",
  assign: "Reassign",
  comment: "Comment",
  "set-due": "Set due date",
};

export const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  "done",
  "declined",
  "cancelled",
]);

/**
 * Client-side mirror of the server's transition table and permission rule
 * (ticket-lifecycle.ts). The server decides; this only decides which buttons
 * to SHOW, so a person is never offered a move that will be refused.
 */
const TRANSITIONS: Record<string, readonly string[]> = {
  ack: ["open"],
  start: ["open", "acknowledged", "waiting_on_requester"],
  wait: ["acknowledged", "in_progress"],
  resume: ["waiting_on_requester"],
  done: ["open", "acknowledged", "in_progress", "waiting_on_requester"],
  decline: ["open", "acknowledged", "in_progress", "waiting_on_requester"],
  cancel: ["open", "acknowledged", "waiting_on_requester"],
  reopen: ["done", "declined", "cancelled"],
};

export interface ActorView {
  id: number;
  team: string | null;
  isOperator: boolean;
}

/**
 * TEAM IDENTITY, THE SAME RULE THE SERVER USES — the JavaScript half of
 * `packages/api/src/services/_shared/team-identity.ts`, mirrored here beside
 * the transition table for the same reason the table is mirrored: so this
 * screen and the server answer the same question the same way.
 *
 * `users.team` and `tickets.team` are free-text columns — nothing normalises
 * what an operator types. The server compares them with `sameTeam` (trimmed
 * and case-folded) and writes `lower(ltrim(rtrim(…)))` into the SQL of its
 * scope query, so to the server "Engineering", "engineering " and
 * "engineering" are ONE team. `===` is neither of those rules: it is a
 * comparison of two strings under a rule nobody chose. The mismatch does not
 * 403 — it does something quieter. The server's scope query puts the ticket in
 * that person's "Waiting on me" and "My team" tabs; `legalActions` then calls
 * the same person a stranger to it, and the drawer offers them no buttons and
 * no explanation, over an action the server would have accepted.
 *
 * THE RULE: a team name compared in this app goes through `teamKey` /
 * `sameTeam`. Not `===`, and not a Map or Set keyed on the raw string —
 * grouping is a comparison too, and two spellings of one team split into two
 * entries with the members divided between them.
 *
 *     grep -rnE "\.team\b" packages/web/src --include=*.ts --include=*.tsx
 *
 * lists every candidate. Anything that matches, narrows, groups or
 * de-duplicates on a team name and is not one of these two is a second
 * definition, and a second definition is the whole mechanism of this defect.
 *
 * `toLowerCase`, never `toLocaleLowerCase`: the latter folds the Turkish
 * dotless i by the browser's locale, which would let two people's browsers
 * disagree about who is on a team.
 *
 * Blank (null, undefined, "", "   ") is not a team but the absence of one, so
 * it keys to null and matches nothing — never another blank. Staff without a
 * team are common here; without that rule every one of them would be on every
 * other one's team.
 */
export const teamKey = (team: string | null | undefined): string | null => {
  const trimmed = (team ?? "").trim();
  return trimmed ? trimmed.toLowerCase() : null;
};

/** Are these two spellings the same team? Blank or null is nobody's team, never a match. */
export const sameTeam = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  const key = teamKey(a);
  return key !== null && key === teamKey(b);
};

export function legalActions(
  actor: ActorView | null,
  t: Pick<TicketRecord, "status" | "requesterId" | "assigneeId" | "team">,
): TicketAction[] {
  if (!actor) return [];
  const statusOk = (a: TicketAction) =>
    !(a in TRANSITIONS) || TRANSITIONS[a].includes(t.status);
  const isAssignee = t.assigneeId != null && t.assigneeId === actor.id;
  const isRequester = t.requesterId === actor.id;
  // `sameTeam`, not `===`: the server's rule, so a ticket the server routed to
  // this person's team offers them the buttons the server would accept even
  // when the two spellings differ in case or padding.
  const onTeam = sameTeam(t.team, actor.team);
  // A UNION, exactly as the server does it (ticket-lifecycle.ts canAct): a
  // person can be requester AND assignee, or requester AND on the team, and
  // each relation is asked. Ranking them would hide buttons the server would
  // in fact accept — a requester assigned to their own request could not
  // cancel it.
  const who = (a: TicketAction): boolean => {
    if (actor.isOperator) return true;
    if (isAssignee && a !== "cancel" && a !== "assign") return true;
    if (onTeam) {
      if (a === "comment") return true;
      if (t.assigneeId == null && a === "ack") return true;
    }
    if (isRequester && (a === "comment" || a === "cancel" || a === "reopen"))
      return true;
    return false;
  };
  return TICKET_ACTIONS.filter((a) => who(a) && statusOk(a));
}

/** The status-moving buttons, in the order they read on a card. */
export const PRIMARY_ACTIONS: readonly TicketAction[] = [
  "ack",
  "start",
  "resume",
  "wait",
  "done",
  "decline",
  "reopen",
  "cancel",
];

/**
 * What the drawer says about a ticket nobody has picked up — since when, and
 * whose queue it is waiting in — or null once somebody has. `holder` is the
 * display name of `assigneeId`, when there is one.
 *
 * A person-only ticket is said to be on ONE desk, because that is the case
 * that went missing (White Board, 2026-10-27): a client ticket that rested on
 * somebody not running the automation, with nothing anywhere saying so.
 */
export function pickupNotice(
  t: Pick<TicketRecord, "awaitingPickupSince" | "assigneeId" | "team">,
  holder: string | null,
): string | null {
  if (!t.awaitingPickupSince) return null;
  const since = when(t.awaitingPickupSince);
  const head = `Nobody has picked this up${since ? ` since ${since}` : ""}: no one has acknowledged it, started it or opened work from it.`;
  const who = holder || "the person it is assigned to";
  if (t.assigneeId != null && t.team) return `${head} It is waiting on ${who} and on team ${t.team}'s queue.`;
  if (t.assigneeId != null) return `${head} It is waiting on ${who} alone: no team queue lists it.`;
  if (t.team) return `${head} It is waiting on team ${t.team}'s queue.`;
  return `${head} It is on nobody's queue.`;
}

/** "Oct 20, 3:05 PM" — a list or timeline timestamp; "" for none or garbage. */
export const when = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/** "Oct 20" — a due date in a list; "" for none or garbage. */
export const dateOnly = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isFinite(d.getTime())
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";
};
